import React, { useEffect, useMemo, useState } from 'react';

import type {
  ChecklistVisita,
  ChecklistVisitaItem,
  EtapaExterna,
} from '../../../api/oficinaExternaApi';
import {
  descartarRascunho,
  lerRascunho,
  salvarRascunho,
} from '../../../infrastructure/offline/oficinaExternaDb';
import { prepareMediaFiles } from '../../../utils/mediaFilePreparation';
import { VIDEO_DA_VISITA_MAX_BYTES } from '../../../api/oficinaExternaApi';
import { notify } from '../../../shared/feedback/notify';
import { hojeIso } from '../formatacao';
import '../../admin-sistema/pages/css/adminSistema.css';
import '../pages/OficinaExterna.css';
import '../../../shared/feedback/aviso-de-tela.css';

export interface DadosDaVisita {
  dataVisita: string;
  etapaId: string;
  /** Entrega total do veículo. */
  previsaoEntrega: string;
  /** Término da etapa atual — a oficina informa as DUAS (pedido do Victor, 25/09/2026). */
  previsaoEtapa: string;
  observacao: string;
  respostas: Record<string, unknown>;
}

interface Props {
  atendimentoRef: string;
  placa: string;
  checklist: ChecklistVisita;
  etapas: EtapaExterna[];
  onCancelar: () => void;
  onSalvar: (dados: DadosDaVisita, fotos: Blob[], videos: VideoDaVisita[]) => Promise<void>;
}

/** at-132: o vídeo escolhido, com o nome (a extensão vira a do objeto no bucket). */
export interface VideoDaVisita {
  blob: Blob;
  nome: string;
}

/** Folga além do próprio vídeo: o resto da fila e o catálogo também moram no aparelho. */
const FOLGA_DE_ESPACO_BYTES = 50 * 1024 * 1024;

/**
 * Cabe no armazenamento do navegador? `null` quando o navegador não sabe dizer — aí o vídeo é
 * aceito, e uma falha ao gravar é avisada pelo próprio salvamento.
 */
const cabeNoAparelho = async (bytes: number): Promise<boolean | null> => {
  try {
    const estimativa = await navigator.storage?.estimate?.();
    if (!estimativa?.quota) return null;
    return estimativa.quota - (estimativa.usage ?? 0) >= bytes + FOLGA_DE_ESPACO_BYTES;
  } catch {
    // Silêncio deliberado: não é chamada de API, e sem a estimativa o vídeo segue aceito.
    return null;
  }
};

const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

const VAZIO: DadosDaVisita = {
  dataVisita: hojeIso(),
  etapaId: '',
  previsaoEntrega: '',
  previsaoEtapa: '',
  observacao: '',
  respostas: {},
};

/**
 * O formulário da visita (at-114, ADR 0059) — o que substitui a página do Samsung Notes.
 *
 * Preenchido em pé, no celular, quase sempre sem rede. Daí três escolhas:
 * - RASCUNHO gravado a cada mudança: o Android descarta a aba em segundo plano sem avisar, e
 *   perder um formulário preenchido no meio da oficina é perder a visita.
 * - Nada é enviado aqui: o salvamento entrega os dados a quem os põe na FILA, e o toast diz
 *   "gravada no aparelho" — nunca "enviada" (regra da at-60).
 * - As fotos passam pelo `prepareMediaFiles`, o mesmo do checklist: HEIC do iPhone vira JPEG e o
 *   arquivo é copiado para a memória, imune a sumir do disco depois.
 */
const VisitaForm: React.FC<Props> = ({
  atendimentoRef,
  placa,
  checklist,
  etapas,
  onCancelar,
  onSalvar,
}) => {
  const [dados, setDados] = useState<DadosDaVisita>(VAZIO);
  const [fotos, setFotos] = useState<Array<{ blob: Blob; url: string }>>([]);
  const [videos, setVideos] = useState<Array<VideoDaVisita & { url: string }>>([]);
  const [salvando, setSalvando] = useState(false);
  const [faltando, setFaltando] = useState<string[]>([]);
  const [rascunhoRecuperado, setRascunhoRecuperado] = useState(false);

  const itens = useMemo(
    () => checklist.itens.filter((i) => i.ativo).sort((a, b) => a.ordem - b.ordem),
    [checklist.itens],
  );

  useEffect(() => {
    let ativo = true;
    void (async () => {
      const rascunho = (await lerRascunho(atendimentoRef).catch(() => null)) as DadosDaVisita | null;
      if (ativo && rascunho) {
        setDados(rascunho);
        setRascunhoRecuperado(true);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [atendimentoRef]);

  // Grava o rascunho a cada mudança. Falha aqui é silenciosa de propósito: o formulário continua
  // preenchido na tela, e um toast a cada tecla seria ruído.
  useEffect(() => {
    void salvarRascunho(atendimentoRef, dados).catch(() => undefined);
  }, [atendimentoRef, dados]);

  useEffect(
    () => () => {
      // Revoga as URLs das miniaturas ao desmontar. Silêncio deliberado: não é chamada de API.
      fotos.forEach((f) => URL.revokeObjectURL(f.url));
    },
    [fotos],
  );

  useEffect(
    () => () => {
      videos.forEach((v) => URL.revokeObjectURL(v.url));
    },
    [videos],
  );

  /**
   * at-132 — vídeos da visita. Até 100 MB cada (decisão do Victor, 25/10/2026), e só se COUBER
   * no armazenamento do aparelho: o vídeo fica guardado nele até haver rede, e aceitar um que
   * não cabe faria a gravação da visita inteira falhar depois, já fora da oficina.
   */
  const anexarVideos = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;

    const preparados = await prepareMediaFiles(Array.from(lista), {
      allowVideo: true,
      allowPdf: false,
    });
    const recusas = preparados.rejected.map((r) => r.file.name);
    const aceitos: Array<VideoDaVisita & { url: string }> = [];
    let somaAceita = videos.reduce((total, v) => total + v.blob.size, 0);

    for (const arquivo of preparados.ready) {
      if (!(arquivo.type || '').startsWith('video/')) {
        recusas.push(arquivo.name);
        continue;
      }
      if (arquivo.size > VIDEO_DA_VISITA_MAX_BYTES) {
        notify.warning(
          `O vídeo ${arquivo.name} tem ${mb(arquivo.size)} e passa do limite de ` +
            `${mb(VIDEO_DA_VISITA_MAX_BYTES)}. Grave um vídeo mais curto.`,
        );
        continue;
      }
      if ((await cabeNoAparelho(somaAceita + arquivo.size)) === false) {
        notify.warning(
          `Não há espaço neste aparelho para guardar o vídeo ${arquivo.name} até a rede voltar. ` +
            'Libere espaço no celular ou grave um vídeo mais curto.',
        );
        continue;
      }
      somaAceita += arquivo.size;
      aceitos.push({ blob: arquivo, nome: arquivo.name, url: URL.createObjectURL(arquivo) });
    }

    if (recusas.length > 0) {
      notify.warning(`Alguns arquivos não puderam ser usados como vídeo: ${recusas.join(', ')}.`);
    }
    setVideos((atuais) => [...atuais, ...aceitos]);
  };

  const responder = (itemId: string, valor: unknown) =>
    setDados((atual) => ({ ...atual, respostas: { ...atual.respostas, [itemId]: valor } }));

  const anexarFotos = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;

    const preparados = await prepareMediaFiles(Array.from(lista), { allowPdf: false });
    if (preparados.rejected.length > 0) {
      notify.warning(
        `Alguns arquivos não puderam ser usados: ${preparados.rejected
          .map((r) => r.file.name)
          .join(', ')}.`,
      );
    }

    setFotos((atuais) => [
      ...atuais,
      ...preparados.ready.map((arquivo) => ({
        blob: arquivo,
        url: URL.createObjectURL(arquivo),
      })),
    ]);
  };

  const validar = (): boolean => {
    const obrigatoriosVazios = itens
      .filter((item) => item.obrigatorio)
      .filter((item) => {
        const valor = dados.respostas[item.id];
        if (valor === undefined || valor === null || valor === '') return true;
        if (Array.isArray(valor) && valor.length === 0) return true;
        return false;
      })
      .map((item) => item.rotulo);

    setFaltando(obrigatoriosVazios);
    return obrigatoriosVazios.length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    setSalvando(true);
    try {
      await onSalvar(
        dados,
        fotos.map((f) => f.blob),
        videos.map(({ blob, nome }) => ({ blob, nome })),
      );
      await descartarRascunho(atendimentoRef).catch(() => undefined);
    } finally {
      setSalvando(false);
    }
  };

  const campoDoItem = (item: ChecklistVisitaItem) => {
    const valor = dados.respostas[item.id];

    switch (item.tipo) {
      case 'SIM_NAO':
        return (
          <div className="oe-sim-nao">
            <button
              type="button"
              className={`oe-chip ${valor === true ? 'selecionado' : ''}`}
              onClick={() => responder(item.id, valor === true ? undefined : true)}
            >
              Sim
            </button>
            <button
              type="button"
              className={`oe-chip ${valor === false ? 'selecionado' : ''}`}
              onClick={() => responder(item.id, valor === false ? undefined : false)}
            >
              Não
            </button>
          </div>
        );

      case 'ESCOLHA_UNICA':
        return (
          <div className="oe-sim-nao" style={{ flexWrap: 'wrap' }}>
            {item.opcoes.map((opcao) => (
              <button
                key={opcao}
                type="button"
                className={`oe-chip ${valor === opcao ? 'selecionado' : ''}`}
                onClick={() => responder(item.id, valor === opcao ? undefined : opcao)}
              >
                {opcao}
              </button>
            ))}
          </div>
        );

      case 'ESCOLHA_MULTIPLA': {
        const selecionadas = Array.isArray(valor) ? (valor as string[]) : [];
        return (
          <div className="oe-sim-nao" style={{ flexWrap: 'wrap' }}>
            {item.opcoes.map((opcao) => {
              const marcada = selecionadas.includes(opcao);
              return (
                <button
                  key={opcao}
                  type="button"
                  className={`oe-chip ${marcada ? 'selecionado' : ''}`}
                  onClick={() =>
                    responder(
                      item.id,
                      marcada
                        ? selecionadas.filter((o) => o !== opcao)
                        : [...selecionadas, opcao],
                    )
                  }
                >
                  {opcao}
                </button>
              );
            })}
          </div>
        );
      }

      case 'NUMERO':
        return (
          <input
            className="admin-input"
            type="number"
            inputMode="decimal"
            value={valor === undefined || valor === null ? '' : String(valor)}
            onChange={(e) =>
              responder(item.id, e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
        );

      case 'DATA':
        return (
          <input
            className="admin-input"
            type="date"
            value={typeof valor === 'string' ? valor : ''}
            onChange={(e) => responder(item.id, e.target.value || undefined)}
          />
        );

      default:
        return (
          <input
            className="admin-input"
            type="text"
            value={typeof valor === 'string' ? valor : ''}
            onChange={(e) => responder(item.id, e.target.value || undefined)}
          />
        );
    }
  };

  return (
    <div className="oe-form">
      {rascunhoRecuperado && (
        <p className="aviso-de-tela">
          Recuperamos o que você estava preenchendo nesta placa. Confira antes de salvar.
        </p>
      )}

      {/* Seção 1 — o que a oficina disse. Três campos numa linha no desktop; no celular o
          `admin-form-grid` empilha. O título da tela diz a ação; a placa é o contexto. */}
      <div className="admin-card">
        <h3 className="admin-card-title">Situação informada pela oficina — {placa}</h3>
        <p className="admin-hint">
          A <strong>etapa</strong> e as <strong>duas previsões</strong> são o que a oficina
          informar agora — registre como eles disseram. Não é acompanhamento em tempo real. A
          previsão <strong>da etapa</strong> é quando a etapa atual termina; a <strong>de entrega
          total</strong> é quando o veículo fica pronto.
        </p>

        <div className="admin-form-grid">
          <div className="admin-input-group curto">
            <label className="admin-label oe-campo-obrigatorio" htmlFor="oe-vis-data">
              Data da visita
            </label>
            <input
              id="oe-vis-data"
              className="admin-input"
              type="date"
              max={hojeIso()}
              value={dados.dataVisita}
              onChange={(e) => setDados({ ...dados, dataVisita: e.target.value })}
            />
          </div>
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="oe-vis-etapa">
              Etapa informada pela oficina
            </label>
            <select
              id="oe-vis-etapa"
              className="admin-select"
              value={dados.etapaId}
              onChange={(e) => setDados({ ...dados, etapaId: e.target.value })}
            >
              <option value="">Não informada</option>
              {etapas.map((etapa) => (
                <option key={etapa.id} value={etapa.id}>
                  {etapa.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* As DUAS previsões, lado a lado: a da etapa atual e a da entrega total. Uma só era
            ambígua — o inspetor não sabia qual das duas datas a oficina estava dando. */}
        <div className="admin-form-grid" style={{ marginTop: 16 }}>
          <div className="admin-input-group curto">
            <label className="admin-label" htmlFor="oe-vis-previsao-etapa">
              Previsão da etapa informada
            </label>
            <input
              id="oe-vis-previsao-etapa"
              className="admin-input"
              type="date"
              value={dados.previsaoEtapa}
              onChange={(e) => setDados({ ...dados, previsaoEtapa: e.target.value })}
            />
          </div>
          <div className="admin-input-group curto">
            <label className="admin-label" htmlFor="oe-vis-previsao">
              Previsão de entrega total informada
            </label>
            <input
              id="oe-vis-previsao"
              className="admin-input"
              type="date"
              value={dados.previsaoEntrega}
              onChange={(e) => setDados({ ...dados, previsaoEntrega: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Seção 2 — o checklist configurado. Só existe quando há pergunta: um card "Checklist"
          vazio sugeriria que algo deixou de carregar. */}
      {itens.length > 0 && (
        <div className="admin-card">
          <h3 className="admin-card-title">Checklist da visita</h3>
          <div className="admin-form-grid" style={{ alignItems: 'flex-start' }}>
            {itens.map((item) => (
              <div className="admin-input-group" key={item.id}>
                <span className={`admin-label ${item.obrigatorio ? 'oe-campo-obrigatorio' : ''}`}>
                  {item.rotulo}
                </span>
                {campoDoItem(item)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seção 3 — o que o inspetor viu, em texto e em foto. */}
      <div className="admin-card">
        <h3 className="admin-card-title">Observações e fotos</h3>

        <div className="admin-input-group">
          <label className="admin-label" htmlFor="oe-vis-observacao">
            Observações
          </label>
          <textarea
            id="oe-vis-observacao"
            className="admin-input"
            rows={4}
            style={{ resize: 'vertical' }}
            value={dados.observacao}
            maxLength={10000}
            placeholder="O que você viu no veículo"
            onChange={(e) => setDados({ ...dados, observacao: e.target.value })}
          />
        </div>

        <div className="admin-input-group" style={{ marginTop: 16 }}>
          <label className="admin-label" htmlFor="oe-vis-fotos">
            Fotos
          </label>
          <input
            id="oe-vis-fotos"
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => {
              void anexarFotos(e.target.files);
              e.target.value = '';
            }}
          />
          {fotos.length > 0 && (
            <div className="oe-miniaturas">
              {fotos.map((foto, indice) => (
                <div className="oe-miniatura" key={foto.url}>
                  <img src={foto.url} alt={`Foto ${indice + 1}`} />
                  <button
                    type="button"
                    aria-label={`Remover foto ${indice + 1}`}
                    onClick={() => {
                      URL.revokeObjectURL(foto.url);
                      setFotos((atuais) => atuais.filter((f) => f.url !== foto.url));
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="admin-input-group" style={{ marginTop: 16 }}>
          <label className="admin-label" htmlFor="oe-vis-videos">
            Vídeos
          </label>
          <input
            id="oe-vis-videos"
            type="file"
            accept="video/*"
            multiple
            capture="environment"
            onChange={(e) => {
              void anexarVideos(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="oe-hint">
            Até {mb(VIDEO_DA_VISITA_MAX_BYTES)} por vídeo. O vídeo fica guardado no aparelho até
            haver rede e é o último item da visita a subir, porque é o mais pesado.
          </p>
          {videos.length > 0 && (
            <div className="oe-miniaturas">
              {videos.map((video, indice) => (
                <div className="oe-miniatura" key={video.url}>
                  <video src={video.url} muted playsInline preload="metadata" />
                  <button
                    type="button"
                    aria-label={`Remover vídeo ${indice + 1}`}
                    onClick={() => {
                      URL.revokeObjectURL(video.url);
                      setVideos((atuais) => atuais.filter((v) => v.url !== video.url));
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {faltando.length > 0 && (
        <div className="erro-de-tela" role="alert">
          Responda antes de salvar: {faltando.join(', ')}.
        </div>
      )}

      <div className="oe-form-rodape">
        <button
          type="button"
          className="admin-btn admin-btn-secondary"
          disabled={salvando}
          onClick={onCancelar}
        >
          Cancelar
        </button>
        <button type="button" className="admin-btn admin-btn-primary" disabled={salvando} onClick={salvar}>
          {salvando ? 'Gravando...' : 'Salvar visita'}
        </button>
      </div>
    </div>
  );
};

export default VisitaForm;
